import { environment } from '../../../../environments';
import { CachedContent } from '../../../interfaces/cached-content';
import { SeoMeta } from '../../../interfaces/seo';
import {
  replaceVariables,
  setCanonicalUrl,
  setMetaProperty,
  setMetaTag,
  setSchemaMarkup,
  validateLength,
} from './meta-tag.utils';
import { SEO_DEFAULTS } from './seo-defaults.config';

/**
 * SEO limits imported from configuration
 */
const SEO_LIMITS = {
  TITLE_MAX: SEO_DEFAULTS.titleMaxLength,
  DESCRIPTION_MAX: SEO_DEFAULTS.descriptionMaxLength,
};

/**
 * Page title mapping for human-readable page names
 * Maps page keys to display-friendly titles
 */
const PAGE_TITLE_MAP: Record<string, string> = {
  home: 'Home',
  default: 'Home',
  faq: 'FAQ',
  'installation-info': 'Installation Guide',
  help: 'Help & Support',
  destination: 'Choose Your Destination',
  supportedDevices: 'Supported Devices',
  howItWorks: 'How It Works',
  termsAndConditions: 'Terms and Conditions',
  privacyPolicy: 'Privacy Policy',
  affiliateProgram: 'Affiliate Program',
  rewardPoints: 'Reward Points',
  countryPage: 'Country eSIM Plans',
  regionPage: 'Regional eSIM Plans',
  globalRegionPage: 'Global eSIM Plans',
};

/**
 * Prepares variables for template replacement
 * Only includes variables that have actual values (no empty strings)
 * @param cacheContent - Cached content with customer configuration
 * @param locationName - Optional location name (for country/region pages)
 * @param pageName - Optional page name/key (e.g., 'destination', 'faq', 'home')
 */
function prepareVariables(
  cacheContent: CachedContent,
  locationName?: string,
  pageName?: string
): Record<string, string> {
  const variables: Record<string, string> = {};

  // Only add customer name if it exists
  const customerName = cacheContent.cache.config.webAppConfig?.name;
  if (customerName) {
    variables['customerName'] = customerName;
  }

  // Only add location name if it exists
  if (locationName) {
    variables['locationName'] = locationName;
  }

  // Add page-specific variables
  if (pageName) {
    variables['pageName'] = pageName;

    // Add human-readable page title if available
    const pageTitle = PAGE_TITLE_MAP[pageName];
    if (pageTitle) {
      variables['pageTitle'] = pageTitle;
    }
  }

  return variables;
}

/**
 * Sets basic SEO meta tags (title, description, keywords, robots, author)
 * Only adds tags if values are provided - no fallbacks for white-label apps
 */
function setBasicMetaTags(
  $document: Document,
  content: SeoMeta,
  variables: Record<string, string>
): { title: string; description: string } {
  // Title - only set if provided
  let title = '';
  if (content.title) {
    // Replace variables in title (handles both already-replaced and template strings)
    // If title is already replaced (e.g., "India eSIM Plans | CustomerName"),
    // replaceVariables will just return it unchanged if no variables match
    title = replaceVariables(content.title, variables);

    title = validateLength(title, SEO_LIMITS.TITLE_MAX);
    const finalTitle = `${title}${environment.previewMode ? ' [PREVIEW]' : ''}`;
    $document.title = finalTitle;
  }

  // Description - only set if provided
  let description = '';
  if (content.description) {
    description = validateLength(
      replaceVariables(content.description, variables),
      SEO_LIMITS.DESCRIPTION_MAX
    );
    setMetaTag($document, 'description', description);
  }

  // Keywords - only set if provided
  if (content.keywords) {
    const keywords = replaceVariables(content.keywords, variables);
    setMetaTag($document, 'keywords', keywords);
  }

  // Robots - only set if specified
  if (content.robots) {
    setMetaTag($document, 'robots', content.robots);
  }

  // Author - only set if specified
  if (content.author) {
    setMetaTag($document, 'author', content.author);
  }

  return { title, description };
}

/**
 * Sets Open Graph meta tags
 * Only adds tags if values are provided - no fallbacks for white-label apps
 */
function setOpenGraphTags(
  $document: Document,
  content: SeoMeta,
  variables: Record<string, string>,
  cacheContent: CachedContent,
  fallbackTitle: string,
  fallbackDescription: string
): string {
  // OG Title (use specific OG title or fallback to main title if available)
  let ogTitle = '';
  if (content.ogTitle) {
    ogTitle = validateLength(replaceVariables(content.ogTitle, variables), SEO_LIMITS.TITLE_MAX);
    setMetaProperty($document, 'og:title', ogTitle);
  } else if (fallbackTitle) {
    ogTitle = fallbackTitle;
    setMetaProperty($document, 'og:title', ogTitle);
  }

  // OG Description (use specific OG description or fallback to main description if available)
  if (content.ogDescription) {
    const ogDescription = validateLength(
      replaceVariables(content.ogDescription, variables),
      SEO_LIMITS.DESCRIPTION_MAX
    );
    setMetaProperty($document, 'og:description', ogDescription);
  } else if (fallbackDescription) {
    setMetaProperty($document, 'og:description', fallbackDescription);
  }

  // OG Image
  if (content.ogImage) {
    const ogImage = replaceVariables(content.ogImage, variables);
    setMetaProperty($document, 'og:image', ogImage);

    if (content.ogImageWidth) {
      setMetaProperty($document, 'og:image:width', content.ogImageWidth);
    }

    if (content.ogImageHeight) {
      setMetaProperty($document, 'og:image:height', content.ogImageHeight);
    }
  }

  // OG URL
  if (content.ogUrl) {
    const ogUrl = replaceVariables(content.ogUrl, variables);
    setMetaProperty($document, 'og:url', ogUrl);
  }

  // OG Type (only if specified - no default for white-label)
  if (content.ogType) {
    setMetaProperty($document, 'og:type', content.ogType);
  }

  // OG Site Name (only if specified or customer name is available)
  if (content.ogSiteName) {
    const ogSiteName = replaceVariables(content.ogSiteName, variables);
    setMetaProperty($document, 'og:site_name', ogSiteName);
  } else if (cacheContent.cache.config.webAppConfig.name) {
    setMetaProperty($document, 'og:site_name', cacheContent.cache.config.webAppConfig.name);
  }

  return ogTitle;
}

/**
 * Sets Twitter Card meta tags
 * Only adds tags if values are provided - no fallbacks for white-label apps
 */
function setTwitterCardTags(
  $document: Document,
  content: SeoMeta,
  variables: Record<string, string>,
  ogTitle: string,
  ogDescription: string
): void {
  // Twitter Card Type (only if specified - no default for white-label)
  if (content.twitterCard) {
    setMetaTag($document, 'twitter:card', content.twitterCard);
  }

  // Twitter Title (use specific Twitter title or fallback to OG title if available)
  if (content.twitterTitle) {
    const twitterTitle = validateLength(
      replaceVariables(content.twitterTitle, variables),
      SEO_LIMITS.TITLE_MAX
    );
    setMetaTag($document, 'twitter:title', twitterTitle);
  } else if (ogTitle) {
    setMetaTag($document, 'twitter:title', ogTitle);
  }

  // Twitter Description (use specific Twitter description or fallback to OG description if available)
  if (content.twitterDescription) {
    const twitterDescription = validateLength(
      replaceVariables(content.twitterDescription, variables),
      SEO_LIMITS.DESCRIPTION_MAX
    );
    setMetaTag($document, 'twitter:description', twitterDescription);
  } else if (ogDescription) {
    setMetaTag($document, 'twitter:description', ogDescription);
  }

  // Twitter Image (fallback to OG image)
  const twitterImage = content.twitterImage || content.ogImage;
  if (twitterImage) {
    const image = replaceVariables(twitterImage, variables);
    setMetaTag($document, 'twitter:image', image);
  }

  // Twitter Site
  if (content.twitterSite) {
    setMetaTag($document, 'twitter:site', content.twitterSite);
  }

  // Twitter Creator
  if (content.twitterCreator) {
    setMetaTag($document, 'twitter:creator', content.twitterCreator);
  }
}

/**
 * Sets canonical URL and schema markup
 */
function setAdditionalSeoElements(
  $document: Document,
  content: SeoMeta,
  variables: Record<string, string>
): void {
  // Canonical URL
  if (content.canonical) {
    const canonical = replaceVariables(content.canonical, variables);
    setCanonicalUrl($document, canonical);
  }

  // Schema Markup (JSON-LD)
  if (content.schemaMarkup) {
    try {
      const schemaMarkupString = JSON.stringify(content.schemaMarkup);
      const replacedSchemaMarkup = replaceVariables(schemaMarkupString, variables, false);
      const schemaMarkup = JSON.parse(replacedSchemaMarkup);
      setSchemaMarkup($document, schemaMarkup);
    } catch (error) {
      console.error('Failed to parse schema markup:', error);
      // Fallback: use schema markup without variable replacement
      setSchemaMarkup($document, content.schemaMarkup);
    }
  }
}

/**
 * Sets all SEO meta tags for a page
 * This function updates existing meta tags instead of appending duplicates
 * Supports: Basic meta tags, Open Graph, Twitter Cards, Canonical URLs, Structured Data
 *
 * @param content - SEO metadata from the JSON file
 * @param cacheContent - Cached content with customer configuration
 * @param $document - Document object to manipulate
 * @param locationName - Optional location name for dynamic replacement
 * @param pageName - Optional page name/key (e.g., 'destination', 'faq', 'home') for page-specific variables
 */
export function appendMetaValues(
  content: SeoMeta,
  cacheContent: CachedContent,
  $document: Document,
  locationName?: string,
  pageName?: string
) {
  const variables = prepareVariables(cacheContent, locationName, pageName);

  const { title, description } = setBasicMetaTags($document, content, variables);

  const ogTitle = setOpenGraphTags($document, content, variables, cacheContent, title, description);

  // Get OG description for Twitter fallback
  const ogDescription = content.ogDescription
    ? validateLength(replaceVariables(content.ogDescription, variables), SEO_LIMITS.DESCRIPTION_MAX)
    : description;

  setTwitterCardTags($document, content, variables, ogTitle, ogDescription);

  setAdditionalSeoElements($document, content, variables);
}
